import {
  DeleteConfirmation,
  ExportDialog,
  SearchInput,
  ExportDropdown,
  Page,
  BackLink,
  IconButton,
  FiltersBar,
  ListView,
  ListViewActions,
  ListViewMenu,
} from '~/components';
import { useApi, useConfirmation, useToast, useWorkspace } from '~/contexts';
import { useDocumentTitle, useFeatures } from '~/hooks';
import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { ErrorPage, PageLoader } from '~/routes/public/pages';
import { mimeTypes } from '~/utils';
import qboIcon from '~/assets/public/qbo-icon.png';
import xeroIcon from '~/assets/public/xero-icon.png';
import sageIcon from '~/assets/public/sage-intacct-icon.svg';
import { ActiveStatusSelect } from '../custom-data/components';
import BusinessUnitDrawer from './BusinessUnitDrawer';

const providerIcons = { qbo: qboIcon, xero: xeroIcon, sage: sageIcon };

const AccountingIntegration = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  overflow: hidden;
`;

const ProviderIcon = styled.img`
  width: 1rem;
  height: 1rem;
  flex-shrink: 0;
`;

const ConnectionName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

function BusinessUnitsPage() {
  const documentTitle = useDocumentTitle('Business Units');

  const api = useApi();
  const { workspace } = useWorkspace();
  const features = useFeatures();
  const confirmation = useConfirmation();
  const toast = useToast();

  const [query, setQuery] = useState({ isReady: false, data: null, error: null });
  const [params, setParams] = useState({ q: '', isActive: 'true' });

  const fetchData = useCallback(async () => {
    try {
      const { data } = await api.www
        .workspaces(workspace.id)
        .businessUnits()
        .get({ ...params, isActive: params.isActive ?? undefined });
      setQuery({ isReady: true, data });
    } catch (error) {
      setQuery({ isReady: true, data: null, error });
    }
  }, [workspace.id, params, api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const [dialog, setDialog] = useState(null);

  const handleFilterChange = ({ target: { name, value } }) => {
    setParams((state) => ({ ...state, [name]: value }));
  };

  const handleExport = async (filename, mimeType) => {
    confirmation.prompt((resolve) => (
      <ExportDialog
        filename={filename}
        onClose={resolve}
        onLoad={api.www
          .workspaces(workspace.id)
          .businessUnits()
          .get(
            { ...params, isActive: params.isActive ?? undefined },
            { headers: { accept: mimeType }, responseType: 'blob' },
          )}
      />
    ));
  };

  const handleEdit = async (businessUnit) => {
    setDialog(
      <BusinessUnitDrawer
        businessUnit={businessUnit}
        onClose={() => {
          setDialog(null);
          documentTitle.set('Business Units');
        }}
        onDeleted={fetchData}
        onSaved={fetchData}
      />,
    );
  };

  async function handleActiveStatusChange(item, flag) {
    try {
      await api.www.workspaces(workspace.id).businessUnits(item.id).setActiveStatus(flag);
      await fetchData();
    } catch ({ message }) {
      toast.error(message);
    }
  }

  async function handleDelete(businessUnit) {
    const confirm = await confirmation.prompt((resolve) => (
      <DeleteConfirmation resolve={resolve}>Are you sure you want to delete this Business Unit?</DeleteConfirmation>
    ));
    if (!confirm) return;

    try {
      await api.www
        .workspaces(workspace.id)
        .businessUnits(businessUnit ? businessUnit.id : undefined)
        .delete();
      await fetchData();
    } catch ({ message }) {
      toast.error(message);
    }
  }

  if (!query.isReady) return <PageLoader />;
  if (!query.data) return <ErrorPage publicSite={false} />;

  const data = query.data;

  return (
    <>
      <Page.Header>
        <BackLink defaultPath={`/app/${workspace.key}/settings/custom-data`} />

        <Page.Info>
          <Page.Title>Business Units</Page.Title>
          <Page.Description>
            Each client and project can be assigned to a business unit. You can set invoice defaults for each business
            unit.
          </Page.Description>
        </Page.Info>

        <Page.Actions>
          <ExportDropdown>
            {({ setIsOpen }) => (
              <>
                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport('business_units.csv', mimeTypes.csv);
                    setIsOpen(false);
                  }}>
                  Export to CSV
                </ExportDropdown.Item>

                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport('business_units.xlsx', mimeTypes.xlsx);
                    setIsOpen(false);
                  }}>
                  Export to Excel
                </ExportDropdown.Item>
              </>
            )}
          </ExportDropdown>

          <IconButton icon="plus" tooltip="New Business Unit" onClick={() => handleEdit({})} />
        </Page.Actions>
      </Page.Header>

      <Page.Filters>
        <FiltersBar>
          <SearchInput value={params.q} placeholder="Search" onChange={handleFilterChange} />

          <ActiveStatusSelect value={params.isActive} onChange={handleFilterChange} />
        </FiltersBar>
      </Page.Filters>

      <Page.ListView>
        <ListView>
          <ListView.Header>
            <ListView.Column name="name">Name</ListView.Column>
            <ListView.Column name="accountingIntegration" isVisible={features.multipleAccountingIntegrations}>
              Accounting Integration
            </ListView.Column>
            <ListViewActions.Column />
          </ListView.Header>
          <ListView.Body>
            {data.map((businessUnit) => {
              const { name, isActive, id } = businessUnit;

              // A business unit with no assignment of its own syncs through the workspace default.
              const accountingProvider = businessUnit.accountingProvider ?? workspace.accountingProvider;
              const accountingConnectionName = businessUnit.accountingProvider
                ? businessUnit.accountingConnectionName
                : workspace.accountingConnectionName && `${workspace.accountingConnectionName} (Workspace Default)`;

              return (
                <ListView.Row
                  data-testid="business_unit_row"
                  onClick={() => handleEdit(businessUnit)}
                  key={id}
                  isDisabled={!isActive}>
                  <ListView.Cell>{name}</ListView.Cell>
                  <ListView.Cell>
                    {accountingProvider && accountingConnectionName && (
                      <AccountingIntegration>
                        <ProviderIcon src={providerIcons[accountingProvider]} />
                        <ConnectionName>{accountingConnectionName}</ConnectionName>
                      </AccountingIntegration>
                    )}
                  </ListView.Cell>
                  <ListViewActions>
                    <>
                      <ListViewActions.Edit onClick={() => handleEdit(businessUnit)} />

                      <hr />

                      <ListViewMenu>
                        {({ setIsOpen }) => (
                          <>
                            <ListViewMenu.Item onClick={() => handleEdit(businessUnit)}>Edit</ListViewMenu.Item>
                            <ListViewMenu.Item
                              onClick={async () => {
                                await handleActiveStatusChange(businessUnit, !isActive);
                                setIsOpen(false);
                              }}>
                              {isActive ? 'Deactivate' : 'Activate'}
                            </ListViewMenu.Item>
                            <ListViewMenu.DeleteItem
                              tooltip="This item is currently in use."
                              onCheckDependencies={async (workspace) =>
                                (await workspace.businessUnits(id).hasDependencies()).data
                              }
                              onClick={() => handleDelete(businessUnit)}>
                              Delete
                            </ListViewMenu.DeleteItem>
                          </>
                        )}
                      </ListViewMenu>
                    </>
                  </ListViewActions>
                </ListView.Row>
              );
            })}

            {data.length === 0 && <ListView.Empty />}
          </ListView.Body>
          <ListView.Status total={data.length} label="Business Unit" />
        </ListView>
      </Page.ListView>

      {dialog}
    </>
  );
}

export default BusinessUnitsPage;
